import React, { useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import {
  billingCaseAmendmentStatusLabels,
  billingCaseAttention,
  billingCaseConsistencyLabels,
  billingCaseKindLabels,
  billingCaseLinkLabels,
  billingCaseProgressPercent,
  billingCaseStatusLabels,
  billingStrategyLabels,
  type BillingCaseAmendment,
  type BillingCaseCostReviewCandidate,
  type BillingCaseConsistencyReview,
  type BillingCaseInvoiceCandidate,
  type BillingCaseInvoicePreparation,
  type BillingCaseLedgerEntry,
  type BillingCaseLink,
  type BillingCaseOperationalSync,
  type BillingCaseProgress,
  type BillingCaseSummary,
} from "./billing-cases";

type NamedEntity = { id: string; name: string };
type NamedProperty = NamedEntity & { client_id: string; address?: string | null };
export type BillingCaseLinkCandidate = {
  id: string;
  entity_type: BillingCaseLink["entity_type"];
  client_id: string;
  label: string;
  detail: string;
};

const money = (value: number | null | undefined) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));

const date = (value: string | null | undefined) =>
  value
    ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(
        new Date(value),
      )
    : "—";

const invoicePreparationLabels: Record<
  BillingCaseInvoicePreparation["document_kind"],
  string
> = {
  progress: "Situation d’avancement",
  final: "Facture de solde",
  credit: "Avoir",
};

export function AdminBillingCases({
  available,
  governanceAvailable,
  operationsAvailable,
  invoicingAvailable,
  cases,
  links,
  progressVersions,
  ledgerEntries,
  amendments,
  consistencyReviews,
  operationalSyncs,
  invoicePreparations,
  invoices,
  costReviewCandidates,
  linkCandidates,
  clients,
  properties,
  reload,
}: {
  available: boolean;
  governanceAvailable: boolean;
  operationsAvailable: boolean;
  invoicingAvailable: boolean;
  cases: BillingCaseSummary[];
  links: BillingCaseLink[];
  progressVersions: BillingCaseProgress[];
  ledgerEntries: BillingCaseLedgerEntry[];
  amendments: BillingCaseAmendment[];
  consistencyReviews: BillingCaseConsistencyReview[];
  operationalSyncs: BillingCaseOperationalSync[];
  invoicePreparations: BillingCaseInvoicePreparation[];
  invoices: BillingCaseInvoiceCandidate[];
  costReviewCandidates: BillingCaseCostReviewCandidate[];
  linkCandidates: BillingCaseLinkCandidate[];
  clients: NamedEntity[];
  properties: NamedProperty[];
  reload: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [caseAction, setCaseAction] = useState<"link" | "progress" | "ledger" | "amendment" | "amendmentDecision" | "operationalSync" | "invoiceDraft" | null>(null);
  const [selectedAmendmentId, setSelectedAmendmentId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [draft, setDraft] = useState({
    clientId: clients[0]?.id || "",
    propertyId: "",
    title: "",
    kind: "simple" as "simple" | "project",
    strategy: "grouped" as BillingCaseSummary["billing_strategy"],
    contractValue: "",
    targetMargin: "",
    notes: "",
  });
  const [progressDraft, setProgressDraft] = useState({
    method: "overall" as BillingCaseProgress["progress_method"],
    percent: "",
    approvedAmount: "",
    status: "draft" as BillingCaseProgress["status"],
    notes: "",
  });
  const [ledgerDraft, setLedgerDraft] = useState({
    direction: "cost" as BillingCaseLedgerEntry["direction"],
    stage: "forecast" as BillingCaseLedgerEntry["stage"],
    category: "",
    description: "",
    quantity: "1",
    unit: "",
    unitAmount: "",
    vatRate: "20",
    date: "",
    confidence: "medium" as BillingCaseLedgerEntry["confidence"],
    supersedesId: "",
  });
  const [linkDraft, setLinkDraft] = useState({
    entityType: "mission" as BillingCaseLink["entity_type"],
    entityId: "",
    role: "primary",
  });
  const [amendmentDraft, setAmendmentDraft] = useState({
    title: "",
    reason: "",
    revisedTotal: "",
    sourceQuoteId: "",
  });
  const [amendmentDecisionNote, setAmendmentDecisionNote] = useState("");
  const [operationalDraft, setOperationalDraft] = useState({
    reviewId: "",
    notes: "",
  });
  const [invoiceDraft, setInvoiceDraft] = useState({
    kind: "progress" as "progress" | "final" | "credit",
    amount: "",
    vatRate: "20",
    sourceInvoiceId: "",
    creditReason: "",
    notes: "",
    idempotencyKey: "",
  });
  const selected = cases.find((item) => item.id === selectedId) || null;
  const selectedLinks = links.filter((item) => item.billing_case_id === selectedId);
  const selectedProgress = progressVersions
    .filter((item) => item.billing_case_id === selectedId)
    .sort((a, b) => b.version - a.version);
  const selectedLedger = ledgerEntries
    .filter((item) => item.billing_case_id === selectedId)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const selectedAmendments = amendments
    .filter((item) => item.billing_case_id === selectedId)
    .sort((a, b) => b.amendment_number - a.amendment_number);
  const selectedConsistencyReviews = consistencyReviews
    .filter((item) => item.billing_case_id === selectedId)
    .sort((a, b) => b.review_number - a.review_number);
  const latestConsistencyReview = selectedConsistencyReviews[0] || null;
  const selectedOperationalSyncs = operationalSyncs
    .filter((item) => item.billing_case_id === selectedId)
    .sort((a, b) => b.sync_number - a.sync_number);
  const selectedInvoicePreparations = invoicePreparations
    .filter((item) => item.billing_case_id === selectedId)
    .sort((a, b) => b.preparation_number - a.preparation_number);
  const selectedInvoiceIds = new Set(
    selectedLinks
      .filter((item) => item.entity_type === "invoice")
      .map((item) => item.entity_id),
  );
  const creditSourceInvoices = invoices.filter(
    (item) =>
      selectedInvoiceIds.has(item.id) &&
      item.document_kind !== "credit" &&
      ["sent", "partially_paid", "paid", "overdue", "credited"].includes(
        item.status,
      ),
  );
  const supersededOperationalSyncIds = new Set(
    selectedOperationalSyncs
      .map((item) => item.supersedes_sync_id)
      .filter((id): id is string => Boolean(id)),
  );
  const selectedAmendment = selectedAmendments.find(
    (item) => item.id === selectedAmendmentId,
  ) || null;
  const supersededLedgerIds = new Set(
    selectedLedger
      .map((item) => item.supersedes_entry_id)
      .filter((id): id is string => Boolean(id)),
  );
  const availableLinkCandidates = useMemo(
    () =>
      linkCandidates.filter(
        (candidate) =>
          candidate.client_id === selected?.client_id &&
          !links.some(
            (link) =>
              link.entity_type === candidate.entity_type &&
              link.entity_id === candidate.id,
          ),
      ),
    [linkCandidates, links, selected?.client_id],
  );
  const sourceQuoteCandidates = useMemo(
    () =>
      linkCandidates.filter(
        (candidate) =>
          candidate.client_id === selected?.client_id &&
          candidate.entity_type === "quote",
      ),
    [linkCandidates, selected?.client_id],
  );
  const missionLabel = (missionId: string) =>
    selectedLinks.find(
      (item) => item.entity_type === "mission" && item.entity_id === missionId,
    )?.label_snapshot ||
    linkCandidates.find(
      (item) => item.entity_type === "mission" && item.id === missionId,
    )?.label ||
    "Mission";
  const eligibleCostReviews = useMemo(() => {
    const linkedMissionIds = new Set(
      selectedLinks
        .filter((item) => item.entity_type === "mission")
        .map((item) => item.entity_id),
    );
    const latestReviewByMission = new Map<string, number>();
    selectedOperationalSyncs.forEach((sync) => {
      const review = costReviewCandidates.find(
        (item) => item.id === sync.mission_costing_review_id,
      );
      if (!review) return;
      latestReviewByMission.set(
        sync.mission_id,
        Math.max(latestReviewByMission.get(sync.mission_id) || 0, review.review_number),
      );
    });
    return costReviewCandidates
      .filter(
        (review) =>
          review.status === "approved" &&
          linkedMissionIds.has(review.mission_id) &&
          review.review_number > (latestReviewByMission.get(review.mission_id) || 0),
      )
      .sort((a, b) => b.review_number - a.review_number);
  }, [costReviewCandidates, selectedLinks, selectedOperationalSyncs]);
  const activeCases = cases.filter((item) => !["cancelled", "archived"].includes(item.status));
  const totalContract = activeCases.reduce(
    (sum, item) => sum + Number(item.contract_value_ex_vat || 0),
    0,
  );
  const totalInvoiced = activeCases.reduce(
    (sum, item) => sum + Number(item.invoiced_ex_vat || 0),
    0,
  );
  const totalMargin = activeCases.reduce(
    (sum, item) => sum + Number(item.expected_margin_ex_vat || 0),
    0,
  );
  const alerts = activeCases.reduce(
    (sum, item) => sum + billingCaseAttention(item).length,
    0,
  );
  const availableProperties = useMemo(
    () => properties.filter((item) => item.client_id === draft.clientId),
    [draft.clientId, properties],
  );

  const openCreate = () => {
    setDraft({
      clientId: clients[0]?.id || "",
      propertyId: "",
      title: "",
      kind: "simple",
      strategy: "grouped",
      contractValue: "",
      targetMargin: "",
      notes: "",
    });
    setMessage("");
    setCreateOpen(true);
  };

  const createCase = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase || !draft.clientId || !draft.title.trim()) return;
    setBusy(true);
    setMessage("");
    const result = await supabase.rpc("create_billing_case", {
      p_client_id: draft.clientId,
      p_title: draft.title.trim(),
      p_case_kind: draft.kind,
      p_property_id: draft.propertyId || null,
      p_billing_strategy: draft.strategy,
      p_contract_value_ex_vat: draft.contractValue
        ? Number(draft.contractValue)
        : null,
      p_target_margin_rate: draft.targetMargin
        ? Number(draft.targetMargin)
        : null,
      p_currency: "EUR",
      p_notes: draft.notes.trim() || null,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setCreateOpen(false);
    setMessage("Dossier créé. Les liens et avancements peuvent maintenant être complétés.");
    reload();
  };

  const openProgress = () => {
    setProgressDraft({
      method: "overall",
      percent: selected?.latest_progress_percent == null
        ? ""
        : String(selected.latest_progress_percent),
      approvedAmount: selected?.latest_approved_amount_ex_vat == null
        ? ""
        : String(selected.latest_approved_amount_ex_vat),
      status: "draft",
      notes: "",
    });
    setActionMessage("");
    setCaseAction("progress");
  };

  const saveProgress = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase || !selected) return;
    if (!progressDraft.percent && !progressDraft.approvedAmount) {
      setActionMessage("Renseignez un pourcentage ou un montant d’avancement.");
      return;
    }
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc("record_billing_case_progress", {
      p_billing_case_id: selected.id,
      p_progress_method: progressDraft.method,
      p_progress_percent: progressDraft.percent
        ? Number(progressDraft.percent)
        : null,
      p_approved_amount_ex_vat: progressDraft.approvedAmount
        ? Number(progressDraft.approvedAmount)
        : null,
      p_breakdown: [],
      p_status: progressDraft.status,
      p_notes: progressDraft.notes.trim() || null,
    });
    setBusy(false);
    if (result.error) {
      setActionMessage(result.error.message);
      return;
    }
    setCaseAction(null);
    setMessage(
      progressDraft.status === "approved"
        ? "Nouvelle version d’avancement validée et historisée."
        : "Nouvelle version d’avancement enregistrée en brouillon.",
    );
    reload();
  };

  const openLedger = () => {
    setLedgerDraft({
      direction: "cost",
      stage: "forecast",
      category: "",
      description: "",
      quantity: "1",
      unit: "",
      unitAmount: "",
      vatRate: "20",
      date: "",
      confidence: "medium",
      supersedesId: "",
    });
    setActionMessage("");
    setCaseAction("ledger");
  };

  const saveLedger = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase || !selected) return;
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc("record_billing_case_ledger_entry", {
      p_billing_case_id: selected.id,
      p_direction: ledgerDraft.direction,
      p_stage: ledgerDraft.stage,
      p_category: ledgerDraft.category.trim(),
      p_description: ledgerDraft.description.trim(),
      p_quantity: Number(ledgerDraft.quantity),
      p_unit_amount_ex_vat: Number(ledgerDraft.unitAmount),
      p_vat_rate: Number(ledgerDraft.vatRate),
      p_unit: ledgerDraft.unit.trim() || null,
      p_expected_on: ledgerDraft.stage === "actual" ? null : ledgerDraft.date || null,
      p_occurred_on: ledgerDraft.stage === "actual" ? ledgerDraft.date || null : null,
      p_source_type: null,
      p_source_id: null,
      p_evidence: {},
      p_confidence: ledgerDraft.confidence,
      p_supersedes_entry_id: ledgerDraft.supersedesId || null,
      p_idempotency_key: `billing-ui:${crypto.randomUUID()}`,
    });
    setBusy(false);
    if (result.error) {
      setActionMessage(result.error.message);
      return;
    }
    setCaseAction(null);
    setMessage("Nouvelle écriture explicative ajoutée sans effacer l’historique.");
    reload();
  };

  const openLink = () => {
    const preferred = availableLinkCandidates[0];
    setLinkDraft({
      entityType: preferred?.entity_type || "mission",
      entityId: preferred?.id || "",
      role: "primary",
    });
    setActionMessage("");
    setCaseAction("link");
  };

  const saveLink = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase || !selected) return;
    const candidate = availableLinkCandidates.find(
      (item) =>
        item.entity_type === linkDraft.entityType &&
        item.id === linkDraft.entityId,
    );
    if (!candidate) {
      setActionMessage("Choisissez un objet disponible pour ce client.");
      return;
    }
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc("link_billing_case_entity", {
      p_billing_case_id: selected.id,
      p_entity_type: candidate.entity_type,
      p_entity_id: candidate.id,
      p_link_role: linkDraft.role.trim() || "primary",
      p_label: candidate.label,
      p_metadata: {},
    });
    setBusy(false);
    if (result.error) {
      setActionMessage(result.error.message);
      return;
    }
    setCaseAction(null);
    setMessage(`${billingCaseLinkLabels[candidate.entity_type]} relié au dossier.`);
    reload();
  };

  const openAmendment = () => {
    setAmendmentDraft({
      title: "",
      reason: "",
      revisedTotal: selected?.contract_value_ex_vat == null
        ? ""
        : String(selected.contract_value_ex_vat),
      sourceQuoteId: "",
    });
    setActionMessage("");
    setCaseAction("amendment");
  };

  const saveAmendment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase || !selected) return;
    if (!amendmentDraft.title.trim() || !amendmentDraft.reason.trim()) {
      setActionMessage("Le titre et le motif de l’avenant sont obligatoires.");
      return;
    }
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc("create_billing_case_amendment", {
      p_billing_case_id: selected.id,
      p_title: amendmentDraft.title.trim(),
      p_reason: amendmentDraft.reason.trim(),
      p_revised_contract_value_ex_vat: Number(amendmentDraft.revisedTotal),
      p_source_quote_id: amendmentDraft.sourceQuoteId || null,
    });
    setBusy(false);
    if (result.error) {
      setActionMessage(result.error.message);
      return;
    }
    setCaseAction(null);
    setMessage(
      "Avenant préparé sans modifier le contrat. Une décision administrative reste obligatoire.",
    );
    reload();
  };

  const openAmendmentDecision = (amendment: BillingCaseAmendment) => {
    setSelectedAmendmentId(amendment.id);
    setAmendmentDecisionNote("");
    setActionMessage("");
    setCaseAction("amendmentDecision");
  };

  const decideAmendment = async (decision: "approve" | "reject") => {
    if (!supabase || !selectedAmendment) return;
    if (!amendmentDecisionNote.trim()) {
      setActionMessage("La note de décision est obligatoire.");
      return;
    }
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc("review_billing_case_amendment", {
      p_amendment_id: selectedAmendment.id,
      p_decision: decision,
      p_decision_note: amendmentDecisionNote.trim(),
    });
    setBusy(false);
    if (result.error) {
      setActionMessage(result.error.message);
      return;
    }
    setSelectedAmendmentId(null);
    setCaseAction(null);
    setMessage(
      decision === "approve"
        ? "Avenant approuvé : le nouveau montant contractuel est actif et l’ancien reste historisé."
        : "Avenant refusé avec son motif ; le contrat reste inchangé.",
    );
    reload();
  };

  const runConsistencyReview = async () => {
    if (!supabase || !selected) return;
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc("run_billing_case_consistency_review", {
      p_billing_case_id: selected.id,
    });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    setMessage("Contrôle de cohérence terminé et photographié dans l’historique.");
    reload();
  };

  const openOperationalSync = () => {
    const preferred = eligibleCostReviews[0];
    setOperationalDraft({ reviewId: preferred?.id || "", notes: "" });
    setActionMessage("");
    setCaseAction("operationalSync");
  };

  const postOperationalSync = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase || !selected || !operationalDraft.reviewId) return;
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc(
      "post_mission_costing_review_to_billing_case",
      {
        p_billing_case_id: selected.id,
        p_review_id: operationalDraft.reviewId,
        p_notes: operationalDraft.notes.trim() || null,
      },
    );
    setBusy(false);
    if (result.error) {
      setActionMessage(result.error.message);
      return;
    }
    setCaseAction(null);
    setMessage(
      "Coûts terrain validés intégrés au dossier sans émission de facture ni envoi externe.",
    );
    reload();
  };

  const invoiceDraftSuggestedAmount = (
    kind: "progress" | "final" | "credit",
  ) => {
    if (!selected) return "";
    if (kind === "progress" && selected.latest_approved_amount_ex_vat != null)
      return String(
        Math.max(
          0,
          Math.round(
            (Number(selected.latest_approved_amount_ex_vat) -
              Number(selected.invoiced_ex_vat || 0)) *
              100,
          ) / 100,
        ),
      );
    if (kind === "final" && selected.remaining_to_invoice_ex_vat != null)
      return String(Number(selected.remaining_to_invoice_ex_vat));
    return "";
  };

  const openInvoiceDraft = () => {
    const kind = selected?.latest_approved_amount_ex_vat != null
      ? "progress"
      : "final";
    setInvoiceDraft({
      kind,
      amount: invoiceDraftSuggestedAmount(kind),
      vatRate: "20",
      sourceInvoiceId: creditSourceInvoices[0]?.id || "",
      creditReason: "",
      notes: "",
      idempotencyKey: `billing-case-invoice:${crypto.randomUUID()}`,
    });
    setActionMessage("");
    setCaseAction("invoiceDraft");
  };

  const changeInvoiceDraftKind = (
    kind: "progress" | "final" | "credit",
  ) => {
    setInvoiceDraft({
      ...invoiceDraft,
      kind,
      amount: invoiceDraftSuggestedAmount(kind),
      sourceInvoiceId:
        kind === "credit"
          ? invoiceDraft.sourceInvoiceId || creditSourceInvoices[0]?.id || ""
          : "",
      creditReason: kind === "credit" ? invoiceDraft.creditReason : "",
    });
  };

  const prepareInvoiceDraft = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase || !selected) return;
    if (invoiceDraft.kind === "credit" && !invoiceDraft.sourceInvoiceId) {
      setActionMessage("Choisissez la facture émise à rectifier.");
      return;
    }
    if (invoiceDraft.kind === "credit" && !invoiceDraft.creditReason.trim()) {
      setActionMessage("Le motif de l’avoir est obligatoire.");
      return;
    }
    setBusy(true);
    setActionMessage("");
    const result = await supabase.rpc("prepare_billing_case_invoice_draft", {
      p_billing_case_id: selected.id,
      p_document_kind: invoiceDraft.kind,
      p_amount_ex_vat:
        invoiceDraft.kind === "final" || !invoiceDraft.amount
          ? null
          : Number(invoiceDraft.amount),
      p_vat_rate: Number(invoiceDraft.vatRate),
      p_source_invoice_id:
        invoiceDraft.kind === "credit" ? invoiceDraft.sourceInvoiceId : null,
      p_credit_reason:
        invoiceDraft.kind === "credit"
          ? invoiceDraft.creditReason.trim()
          : null,
      p_notes: invoiceDraft.notes.trim() || null,
      p_idempotency_key: invoiceDraft.idempotencyKey,
    });
    setBusy(false);
    if (result.error) {
      setActionMessage(result.error.message);
      return;
    }
    setCaseAction(null);
    setMessage(
      `${invoicePreparationLabels[invoiceDraft.kind]} préparé(e) en brouillon interne. Aucun numéro officiel, envoi client ou appel Henrri n’a été déclenché.`,
    );
    reload();
  };

  if (!available)
    return (
      <section className="panel billing-case-unavailable">
        <h2>Dossiers de facturation et chantiers</h2>
        <p>
          La fondation a été validée sur la recette isolée, mais elle n’est pas
          encore installée sur cette base. Aucune liste vide n’est présentée
          comme une fonctionnalité active.
        </p>
        <span className="data-warning">Capacité en attente de promotion contrôlée</span>
      </section>
    );

  return (
    <>
      <section className="billing-case-workspace">
        <div className="section-heading-row">
          <div>
            <h2>Dossiers de facturation et chantiers</h2>
            <p className="section-intro">
              Une lecture unique du contrat, de l’avancement, de la production,
              des coûts, de la marge, des factures et des encaissements.
            </p>
          </div>
          <button type="button" onClick={openCreate}>＋ Nouveau dossier</button>
        </div>
        <div className="stats billing-case-stats">
          <article><span>Dossiers actifs</span><strong>{activeCases.length}</strong><small>Simples et chantiers</small></article>
          <article><span>Valeur contractuelle</span><strong>{money(totalContract)}</strong><small>Hors taxes</small></article>
          <article><span>Déjà facturé</span><strong>{money(totalInvoiced)}</strong><small>Hors taxes</small></article>
          <article><span>Marge prévisionnelle</span><strong>{money(totalMargin)}</strong><small>Selon les données disponibles</small></article>
          <article><span>Points d’attention</span><strong>{alerts}</strong><small>À contrôler humainement</small></article>
        </div>
        {message && <p className="notice success">{message}</p>}
        <div className="billing-case-list">
          {cases.map((item) => {
            const client = clients.find((value) => value.id === item.client_id);
            const property = properties.find((value) => value.id === item.property_id);
            const attention = billingCaseAttention(item);
            const progress = billingCaseProgressPercent(item);
            return (
              <article key={item.id}>
                <div className="billing-case-card-heading">
                  <div>
                    <span>{item.case_code}</span>
                    <h3>{item.title}</h3>
                    <small>{client?.name || "Client"}{property ? ` · ${property.name}` : ""}</small>
                  </div>
                  <span className={`status ${item.status}`}>
                    {billingCaseStatusLabels[item.status]}
                  </span>
                </div>
                <div className="billing-case-progress" aria-label={`Avancement ${progress.toFixed(0)} %`}>
                  <i style={{ width: `${progress}%` }} />
                </div>
                <div className="billing-case-card-metrics">
                  <span><small>Avancement</small><b>{progress.toFixed(0)} %</b></span>
                  <span><small>Facturé HT</small><b>{money(item.invoiced_ex_vat)}</b></span>
                  <span><small>Encaissé TTC</small><b>{money(item.paid_inc_vat)}</b></span>
                  <span><small>Marge prévue</small><b>{money(item.expected_margin_ex_vat)}</b></span>
                </div>
                {attention.length > 0 && (
                  <p className="notice warning">{attention[0]}{attention.length > 1 ? ` · +${attention.length - 1} autre(s)` : ""}</p>
                )}
                <div className="row-actions">
                  <small>{billingCaseKindLabels[item.case_kind]} · {billingStrategyLabels[item.billing_strategy]}</small>
                  <button type="button" className="secondary" onClick={() => setSelectedId(item.id)}>
                    Ouvrir la vue 360°
                  </button>
                </div>
              </article>
            );
          })}
          {!cases.length && <p className="empty">Aucun dossier. Créez le premier dossier simple ou chantier.</p>}
        </div>
      </section>

      {selected && (
        <div className="portal-overlay" onMouseDown={() => setSelectedId(null)}>
          <section
            className="portal-detail billing-case-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Vue 360 du dossier de facturation"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="icon-close" aria-label="Fermer" onClick={() => setSelectedId(null)}>×</button>
            <span className="eyebrow">{selected.case_code}</span>
            <h2>{selected.title}</h2>
            <p>
              {clients.find((item) => item.id === selected.client_id)?.name || "Client"}
              {selected.property_id ? ` · ${properties.find((item) => item.id === selected.property_id)?.name || "Bien"}` : ""}
            </p>
            <div className="stats billing-case-detail-stats">
              <article><span>Contrat HT</span><strong>{selected.contract_value_ex_vat == null ? "À compléter" : money(selected.contract_value_ex_vat)}</strong></article>
              <article><span>Reste à facturer HT</span><strong>{selected.remaining_to_invoice_ex_vat == null ? "—" : money(selected.remaining_to_invoice_ex_vat)}</strong></article>
              <article><span>Reste à encaisser TTC</span><strong>{money(selected.remaining_to_collect_inc_vat)}</strong></article>
              <article><span>Coûts prévus HT</span><strong>{money(selected.expected_cost_ex_vat)}</strong></article>
              <article><span>Coûts réels HT</span><strong>{money(selected.actual_cost_ex_vat)}</strong></article>
              <article><span>Marge réelle HT</span><strong>{money(selected.actual_margin_ex_vat)}</strong></article>
            </div>
            <details className="billing-case-kpi-explanation" data-testid="billing-case-kpi-explanation">
              <summary>Comprendre ces montants</summary>
              <p className="section-intro">
                Cette lecture de pilotage se recalcule à partir du contrat, des avenants approuvés,
                des factures et règlements reliés ainsi que des revenus et coûts actifs du dossier.
              </p>
              <div className="billing-case-kpi-explanation-grid">
                <article>
                  <strong>Contrat HT</strong>
                  <span>Montant contractuel initial, remplacé par le nouveau total lorsqu’un avenant est approuvé.</span>
                  <small>Source : dossier et avenants validés.</small>
                </article>
                <article>
                  <strong>Reste à facturer HT</strong>
                  <span>Contrat HT − factures HT émises + avoirs HT.</span>
                  <small>Le montant ne descend jamais sous zéro. Sans contrat renseigné, aucun solde fiable n’est affiché.</small>
                </article>
                <article>
                  <strong>Reste à encaisser TTC</strong>
                  <span>Factures TTC émises − avoirs TTC − règlements rapprochés.</span>
                  <small>Le montant ne descend jamais sous zéro et dépend des pièces effectivement reliées.</small>
                </article>
                <article>
                  <strong>Coûts prévus HT</strong>
                  <span>Somme des coûts actifs prévus, engagés ou déjà réalisés.</span>
                  <small>Une écriture remplacée par une version plus récente sort automatiquement du total.</small>
                </article>
                <article>
                  <strong>Coûts réels HT</strong>
                  <span>Somme des seuls coûts actifs confirmés comme réalisés.</span>
                  <small>Les coûts terrain n’entrent ici qu’après validation et intégration au dossier.</small>
                </article>
                <article>
                  <strong>Marge réelle HT</strong>
                  <span>Revenus réels HT − coûts réels HT.</span>
                  <small>Une donnée non reliée ou encore prévisionnelle n’entre pas dans cette marge réelle.</small>
                </article>
              </div>
              <p className="billing-case-kpi-limit">
                Limite : cette synthèse aide à décider, mais ne remplace pas la validation comptable des pièces et rapprochements.
              </p>
            </details>
            {!governanceAvailable && (
              <p className="notice warning">
                Les avenants et contrôles de cohérence sont validés en recette,
                mais pas encore activés sur cette base.
              </p>
            )}
            {!operationsAvailable && (
              <p className="notice warning">
                Le raccord des coûts terrain est validé en recette, mais pas
                encore activé sur cette base.
              </p>
            )}
            {!invoicingAvailable && (
              <p className="notice warning">
                La préparation contrôlée des situations, soldes et avoirs est
                validée en recette, mais pas encore activée sur cette base.
              </p>
            )}
            {governanceAvailable && latestConsistencyReview && (
              <section className={`notice ${latestConsistencyReview.severity === "clean" ? "success" : latestConsistencyReview.severity === "blocking" ? "error" : "warning"}`}>
                <strong>
                  Contrôle n°{latestConsistencyReview.review_number} · {billingCaseConsistencyLabels[latestConsistencyReview.severity]}
                </strong>
                <span>
                  {latestConsistencyReview.blocking_count} blocage(s) · {latestConsistencyReview.warning_count} avertissement(s) · {date(latestConsistencyReview.created_at)}
                </span>
              </section>
            )}
            {billingCaseAttention(selected).map((alert) => (
              <p className="notice warning" key={alert}>{alert}</p>
            ))}
            <div className="row-actions billing-case-primary-actions">
              <button type="button" className="secondary" onClick={openLink}>＋ Relier un objet</button>
              <button type="button" onClick={openProgress}>＋ Nouvel avancement</button>
              <button type="button" className="secondary" onClick={openLedger}>＋ Revenu ou coût</button>
              {operationsAvailable && (
                <button
                  type="button"
                  className="secondary"
                  onClick={openOperationalSync}
                  disabled={!eligibleCostReviews.length}
                  title={!eligibleCostReviews.length ? "Aucune comparaison approuvée plus récente pour les missions reliées" : undefined}
                >
                  ＋ Intégrer les coûts terrain
                </button>
              )}
              {invoicingAvailable && (
                <button type="button" onClick={openInvoiceDraft}>
                  ＋ Préparer une facture
                </button>
              )}
              {governanceAvailable && (
                <>
                  <button type="button" className="secondary" onClick={openAmendment}>＋ Nouvel avenant</button>
                  <button type="button" disabled={busy} onClick={runConsistencyReview}>
                    {busy ? "Contrôle…" : "Contrôler la cohérence"}
                  </button>
                </>
              )}
            </div>
            {invoicingAvailable && (
              <section data-testid="billing-case-invoice-preparations">
                <h3>Situations, solde et avoirs préparés</h3>
                <p className="section-intro">
                  Chaque brouillon est calculé sur le contrat et l’avancement
                  du dossier. Il reste interne jusqu’à votre relecture et son
                  émission explicite par le fournisseur de facturation.
                </p>
                {selectedInvoicePreparations.map((item) => (
                  <div className="billing-case-history-row" key={item.id}>
                    <b>
                      Préparation n°{item.preparation_number} · {invoicePreparationLabels[item.document_kind]}
                    </b>
                    <span>
                      {money(item.prepared_amount_ex_vat)} HT · TVA {Number(item.vat_rate).toLocaleString("fr-FR")} % · {money(item.total_inc_vat)} TTC
                    </span>
                    <small>
                      {item.invoice_number || "Brouillon sans numéro officiel"} · {item.invoice_status} · {date(item.created_at)}
                    </small>
                    {item.source_progress_version != null && (
                      <small>Avancement approuvé version {item.source_progress_version} · cible cumulée {money(item.target_cumulative_ex_vat)}</small>
                    )}
                    {item.source_invoice_id && (
                      <small>Corrige {item.source_invoice_number || "la facture source"}</small>
                    )}
                    {item.notes && <small>{item.notes}</small>}
                  </div>
                ))}
                {!selectedInvoicePreparations.length && (
                  <p className="empty">Aucun brouillon contrôlé préparé depuis ce dossier.</p>
                )}
              </section>
            )}
            <div className="billing-case-detail-grid">
              <section>
                <h3>Objets reliés</h3>
                {selectedLinks.map((item) => (
                  <div className="billing-case-history-row" key={item.id}>
                    <b>{billingCaseLinkLabels[item.entity_type]}</b>
                    <span>{item.label_snapshot || item.entity_id}</span>
                    <small>{item.link_role} · {date(item.linked_at)}</small>
                  </div>
                ))}
                {!selectedLinks.length && <p className="empty">Aucun objet encore relié.</p>}
              </section>
              <section>
                <h3>Historique d’avancement</h3>
                {selectedProgress.map((item) => (
                  <div className="billing-case-history-row" key={item.id}>
                    <b>Version {item.version} · {item.progress_method}</b>
                    <span>{item.progress_percent == null ? "—" : `${item.progress_percent} %`} · {item.approved_amount_ex_vat == null ? "Montant non défini" : money(item.approved_amount_ex_vat)}</span>
                    <small>{item.status === "approved" ? "Validé" : item.status === "rejected" ? "Refusé" : "Brouillon"} · {date(item.approved_at || item.created_at)}</small>
                  </div>
                ))}
                {!selectedProgress.length && <p className="empty">Aucun avancement enregistré.</p>}
              </section>
            </div>
            {governanceAvailable && (
              <div className="billing-case-detail-grid" data-testid="billing-case-governance">
                <section>
                  <h3>Avenants contractuels</h3>
                  {selectedAmendments.map((item) => (
                    <div className="billing-case-history-row" key={item.id}>
                      <b>Avenant n°{item.amendment_number} · {billingCaseAmendmentStatusLabels[item.status]}</b>
                      <span>
                        {item.title} · {money(item.previous_contract_value_ex_vat)} → {money(item.revised_contract_value_ex_vat)}
                      </span>
                      <small>
                        Écart {item.change_amount_ex_vat >= 0 ? "+" : ""}{money(item.change_amount_ex_vat)} · {date(item.decided_at || item.created_at)}
                      </small>
                      {item.status === "pending_approval" && (
                        <button type="button" className="secondary" onClick={() => openAmendmentDecision(item)}>
                          Relire et décider
                        </button>
                      )}
                    </div>
                  ))}
                  {!selectedAmendments.length && (
                    <p className="empty">Aucun avenant : le montant contractuel initial reste la référence.</p>
                  )}
                </section>
                <section>
                  <h3>Contrôles de cohérence</h3>
                  {latestConsistencyReview?.findings.map((finding) => (
                    <div className="billing-case-history-row" key={finding.code}>
                      <b>{finding.severity === "blocking" ? "Blocage" : "Avertissement"} · {finding.title}</b>
                      <span>{finding.detail}</span>
                      <small>Action : {finding.action}</small>
                    </div>
                  ))}
                  {latestConsistencyReview?.severity === "clean" && (
                    <p className="notice success">Devis acceptés, contrat, avancement, factures et encaissements sont cohérents à cette date.</p>
                  )}
                  {!latestConsistencyReview && (
                    <p className="empty">Aucun contrôle photographié. Lancez le contrôle avant une nouvelle émission.</p>
                  )}
                </section>
              </div>
            )}
            {operationsAvailable && (
              <section data-testid="billing-case-operational-costs">
                <h3>Coûts opérationnels validés</h3>
                <p className="section-intro">
                  Seules les comparaisons de missions approuvées alimentent ces
                  coûts réels. Une nouvelle version remplace la précédente dans
                  les totaux sans effacer l’historique.
                </p>
                {eligibleCostReviews.length > 0 && (
                  <p className="notice warning">
                    {eligibleCostReviews.length} comparaison(s) approuvée(s) prête(s) à intégrer.
                  </p>
                )}
                {selectedOperationalSyncs.map((item) => {
                  const review = costReviewCandidates.find(
                    (candidate) => candidate.id === item.mission_costing_review_id,
                  );
                  return (
                    <div
                      className={`billing-case-history-row ${supersededOperationalSyncIds.has(item.id) ? "billing-case-superseded" : ""}`}
                      key={item.id}
                    >
                      <b>
                        {missionLabel(item.mission_id)} · version terrain n°{review?.review_number || item.sync_number}
                      </b>
                      <span>{money(item.total_actual_cost_ex_vat)} · {item.component_count} composante(s)</span>
                      <small>
                        {date(item.created_at)}
                        {supersededOperationalSyncIds.has(item.id) ? " · Remplacée par une version plus récente" : " · Version active"}
                      </small>
                      {item.notes && <small>{item.notes}</small>}
                    </div>
                  );
                })}
                {!selectedOperationalSyncs.length && (
                  <p className="empty">Aucun coût terrain validé intégré à ce dossier.</p>
                )}
              </section>
            )}
            <section>
              <h3>Explication des revenus et coûts</h3>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Nature</th><th>Étape</th><th>Description</th><th>Montant HT</th><th>Source</th><th>Confiance</th></tr></thead>
                  <tbody>
                    {selectedLedger.map((item) => (
                      <tr className={supersededLedgerIds.has(item.id) ? "billing-case-superseded" : ""} key={item.id}>
                        <td>{item.direction === "revenue" ? "Revenu" : "Coût"}</td>
                        <td>{item.stage}</td>
                        <td>{item.description}{supersededLedgerIds.has(item.id) ? " (remplacé)" : ""}</td>
                        <td>{money(item.amount_ex_vat)}</td>
                        <td>{item.source_type === "mission_costing_sync" ? "Coût terrain validé" : item.source_type || "Saisie interne"}</td>
                        <td>{item.confidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!selectedLedger.length && <p className="empty">Aucun revenu ou coût explicatif.</p>}
            </section>
            <p className="section-intro">
              Fournisseur officiel : {selected.billing_provider || "non figé"}. La numérotation et l’émission restent confiées au fournisseur choisi après relecture humaine.
            </p>
          </section>
        </div>
      )}

      {selected && caseAction === "link" && (
        <div className="portal-overlay billing-case-action-overlay" onMouseDown={() => setCaseAction(null)}>
          <form
            className="portal-detail billing-case-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Relier un objet au dossier"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveLink}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCaseAction(null)}>×</button>
            <h2>Relier un objet existant</h2>
            <p className="section-intro">Seuls les objets du même client et encore libres sont proposés. Le serveur revérifie cette cohérence avant l’enregistrement.</p>
            <div className="form-grid">
              <label>Type<select value={linkDraft.entityType} onChange={(event) => {
                const entityType = event.target.value as BillingCaseLink["entity_type"];
                const first = availableLinkCandidates.find((item) => item.entity_type === entityType);
                setLinkDraft({ ...linkDraft, entityType, entityId: first?.id || "" });
              }}>{(["mission", "quote", "invoice", "expense", "payment"] as const).map((type) => <option value={type} key={type}>{billingCaseLinkLabels[type]}</option>)}</select></label>
              <label>Rôle dans le dossier<input required value={linkDraft.role} onChange={(event) => setLinkDraft({ ...linkDraft, role: event.target.value })} placeholder="production, devis initial, acompte…" /></label>
              <label className="wide">Objet<select required value={linkDraft.entityId} onChange={(event) => setLinkDraft({ ...linkDraft, entityId: event.target.value })}><option value="">Choisir</option>{availableLinkCandidates.filter((item) => item.entity_type === linkDraft.entityType).map((item) => <option value={item.id} key={item.id}>{item.label} · {item.detail}</option>)}</select></label>
              {!availableLinkCandidates.some((item) => item.entity_type === linkDraft.entityType) && <p className="notice warning wide">Aucun objet libre de ce type pour ce client.</p>}
              {actionMessage && <p className="notice error wide">{actionMessage}</p>}
              <button disabled={busy || !linkDraft.entityId}>{busy ? "Liaison…" : "Relier au dossier"}</button>
            </div>
          </form>
        </div>
      )}

      {selected && caseAction === "progress" && (
        <div className="portal-overlay billing-case-action-overlay" onMouseDown={() => setCaseAction(null)}>
          <form
            className="portal-detail billing-case-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Nouvel avancement du dossier"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveProgress}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCaseAction(null)}>×</button>
            <h2>Nouvel avancement</h2>
            <p className="section-intro">Une nouvelle version est créée. Les versions précédentes restent consultables.</p>
            <div className="form-grid">
              <label>Méthode<select value={progressDraft.method} onChange={(event) => setProgressDraft({ ...progressDraft, method: event.target.value as BillingCaseProgress["progress_method"] })}><option value="overall">Global</option><option value="amount">Montant</option><option value="quantity">Quantité</option><option value="line">Ligne</option><option value="lot">Lot</option><option value="phase">Phase</option></select></label>
              <label>État<select value={progressDraft.status} onChange={(event) => setProgressDraft({ ...progressDraft, status: event.target.value as BillingCaseProgress["status"] })}><option value="draft">Brouillon à contrôler</option><option value="approved">Validé par l’administrateur</option></select></label>
              <label>Avancement (%)<input type="number" min="0" max="100" step="0.01" value={progressDraft.percent} onChange={(event) => setProgressDraft({ ...progressDraft, percent: event.target.value })} /></label>
              <label>Montant approuvé HT<input type="number" min="0" step="0.01" value={progressDraft.approvedAmount} onChange={(event) => setProgressDraft({ ...progressDraft, approvedAmount: event.target.value })} /></label>
              <label className="wide">Observations<textarea value={progressDraft.notes} onChange={(event) => setProgressDraft({ ...progressDraft, notes: event.target.value })} /></label>
              {actionMessage && <p className="notice error wide">{actionMessage}</p>}
              <button disabled={busy}>{busy ? "Enregistrement…" : "Créer cette version"}</button>
            </div>
          </form>
        </div>
      )}

      {selected && caseAction === "ledger" && (
        <div className="portal-overlay billing-case-action-overlay" onMouseDown={() => setCaseAction(null)}>
          <form
            className="portal-detail billing-case-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Nouvelle écriture de revenu ou coût"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveLedger}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCaseAction(null)}>×</button>
            <h2>Expliquer un revenu ou un coût</h2>
            <p className="section-intro">Pour corriger une estimation, créez une nouvelle écriture qui la remplace : l’ancienne reste visible et n’est plus comptée.</p>
            <div className="form-grid">
              <label>Nature<select value={ledgerDraft.direction} onChange={(event) => setLedgerDraft({ ...ledgerDraft, direction: event.target.value as BillingCaseLedgerEntry["direction"], supersedesId: "" })}><option value="revenue">Revenu</option><option value="cost">Coût</option></select></label>
              <label>Étape<select value={ledgerDraft.stage} onChange={(event) => setLedgerDraft({ ...ledgerDraft, stage: event.target.value as BillingCaseLedgerEntry["stage"] })}><option value="forecast">Prévision</option><option value="committed">Engagé</option><option value="actual">Réel</option></select></label>
              <label>Catégorie<input required value={ledgerDraft.category} onChange={(event) => setLedgerDraft({ ...ledgerDraft, category: event.target.value })} placeholder="Ex. sous-traitance, matériel" /></label>
              <label>Date<input type="date" value={ledgerDraft.date} onChange={(event) => setLedgerDraft({ ...ledgerDraft, date: event.target.value })} /></label>
              <label className="wide">Description<input required value={ledgerDraft.description} onChange={(event) => setLedgerDraft({ ...ledgerDraft, description: event.target.value })} /></label>
              <label>Quantité<input required type="number" min="0.0001" step="0.0001" value={ledgerDraft.quantity} onChange={(event) => setLedgerDraft({ ...ledgerDraft, quantity: event.target.value })} /></label>
              <label>Unité<input value={ledgerDraft.unit} onChange={(event) => setLedgerDraft({ ...ledgerDraft, unit: event.target.value })} placeholder="heure, forfait, litre…" /></label>
              <label>Prix unitaire HT<input required type="number" min="0" step="0.0001" value={ledgerDraft.unitAmount} onChange={(event) => setLedgerDraft({ ...ledgerDraft, unitAmount: event.target.value })} /></label>
              <label>TVA (%)<input required type="number" min="0" max="100" step="0.01" value={ledgerDraft.vatRate} onChange={(event) => setLedgerDraft({ ...ledgerDraft, vatRate: event.target.value })} /></label>
              <label>Fiabilité<select value={ledgerDraft.confidence} onChange={(event) => setLedgerDraft({ ...ledgerDraft, confidence: event.target.value as BillingCaseLedgerEntry["confidence"] })}><option value="low">Faible</option><option value="medium">À confirmer</option><option value="high">Élevée</option><option value="verified">Vérifiée</option></select></label>
              <label className="wide">Remplace une écriture<select value={ledgerDraft.supersedesId} onChange={(event) => setLedgerDraft({ ...ledgerDraft, supersedesId: event.target.value })}><option value="">Non, nouvelle information</option>{selectedLedger.filter((item) => item.direction === ledgerDraft.direction && !supersededLedgerIds.has(item.id)).map((item) => <option value={item.id} key={item.id}>{item.description} · {money(item.amount_ex_vat)} · {item.stage}</option>)}</select></label>
              {actionMessage && <p className="notice error wide">{actionMessage}</p>}
              <button disabled={busy}>{busy ? "Enregistrement…" : "Ajouter l’écriture"}</button>
            </div>
          </form>
        </div>
      )}

      {selected && caseAction === "operationalSync" && (
        <div className="portal-overlay billing-case-action-overlay" onMouseDown={() => setCaseAction(null)}>
          <form
            className="portal-detail billing-case-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Intégrer les coûts terrain validés"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={postOperationalSync}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCaseAction(null)}>×</button>
            <h2>Intégrer les coûts terrain validés</h2>
            <p className="section-intro">
              Les heures, produits, matériel, déplacements et autres frais
              approuvés deviennent les coûts réels du dossier. Une correction
              ultérieure remplacera cette version en conservant la traçabilité.
              Cette action n’émet aucune facture et ne contacte aucun tiers.
            </p>
            <div className="form-grid">
              <label className="wide">
                Comparaison approuvée
                <select
                  required
                  value={operationalDraft.reviewId}
                  onChange={(event) => setOperationalDraft({ ...operationalDraft, reviewId: event.target.value })}
                >
                  <option value="">Choisir</option>
                  {eligibleCostReviews.map((review) => (
                    <option value={review.id} key={review.id}>
                      {missionLabel(review.mission_id)} · version {review.review_number} · {money(review.actual_total_cost_ex_vat)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                Note d’intégration
                <textarea
                  value={operationalDraft.notes}
                  onChange={(event) => setOperationalDraft({ ...operationalDraft, notes: event.target.value })}
                  placeholder="Contrôles réalisés ou contexte utile au dossier"
                />
              </label>
              {actionMessage && <p className="notice error wide">{actionMessage}</p>}
              <button disabled={busy || !operationalDraft.reviewId}>
                {busy ? "Intégration…" : "Intégrer au dossier"}
              </button>
            </div>
          </form>
        </div>
      )}

      {selected && caseAction === "invoiceDraft" && (
        <div className="portal-overlay billing-case-action-overlay" onMouseDown={() => setCaseAction(null)}>
          <form
            className="portal-detail billing-case-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Préparer une facture du dossier"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={prepareInvoiceDraft}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCaseAction(null)}>×</button>
            <h2>Préparer une pièce de facturation</h2>
            <p className="section-intro">
              ARD Manager contrôle le contrat, l’avancement déjà approuvé, les
              brouillons existants et les avoirs antérieurs. Cette étape ne
              réserve aucun numéro officiel et n’envoie rien au client ni à Henrri.
            </p>
            <div className="form-grid">
              <label>
                Type de pièce
                <select
                  value={invoiceDraft.kind}
                  onChange={(event) => changeInvoiceDraftKind(event.target.value as "progress" | "final" | "credit")}
                >
                  <option value="progress">Situation d’avancement</option>
                  <option value="final">Facture de solde</option>
                  <option value="credit">Avoir</option>
                </select>
              </label>
              <label>
                TVA (%)
                <input
                  required
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={invoiceDraft.vatRate}
                  onChange={(event) => setInvoiceDraft({ ...invoiceDraft, vatRate: event.target.value })}
                />
              </label>
              {invoiceDraft.kind !== "final" ? (
                <label>
                  Montant HT
                  <input
                    required={invoiceDraft.kind === "credit"}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={invoiceDraft.amount}
                    onChange={(event) => setInvoiceDraft({ ...invoiceDraft, amount: event.target.value })}
                  />
                  <small>
                    {invoiceDraft.kind === "progress"
                      ? "La proposition correspond au montant approuvé encore non couvert."
                      : "Le serveur limite l’avoir au solde rectifiable de la facture source."}
                  </small>
                </label>
              ) : (
                <p className="notice wide">
                  Solde calculé automatiquement : {selected.remaining_to_invoice_ex_vat == null ? "montant contractuel manquant" : money(selected.remaining_to_invoice_ex_vat)} HT.
                </p>
              )}
              {invoiceDraft.kind === "credit" && (
                <>
                  <label className="wide">
                    Facture émise à rectifier
                    <select
                      required
                      value={invoiceDraft.sourceInvoiceId}
                      onChange={(event) => setInvoiceDraft({ ...invoiceDraft, sourceInvoiceId: event.target.value })}
                    >
                      <option value="">Choisir</option>
                      {creditSourceInvoices.map((invoice) => (
                        <option value={invoice.id} key={invoice.id}>
                          {invoice.number || "Facture sans numéro"} · {money(invoice.total_ex_vat)} HT · {invoice.status}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="wide">
                    Motif de l’avoir
                    <input
                      required
                      value={invoiceDraft.creditReason}
                      onChange={(event) => setInvoiceDraft({ ...invoiceDraft, creditReason: event.target.value })}
                      placeholder="Erreur de quantité, annulation partielle…"
                    />
                  </label>
                </>
              )}
              <label className="wide">
                Note interne de préparation
                <textarea
                  value={invoiceDraft.notes}
                  onChange={(event) => setInvoiceDraft({ ...invoiceDraft, notes: event.target.value })}
                  placeholder="Contrôles effectués ou contexte utile avant émission"
                />
              </label>
              {actionMessage && <p className="notice error wide">{actionMessage}</p>}
              <button disabled={busy || (invoiceDraft.kind === "credit" && !creditSourceInvoices.length)}>
                {busy ? "Préparation…" : "Créer le brouillon contrôlé"}
              </button>
            </div>
          </form>
        </div>
      )}

      {selected && caseAction === "amendment" && (
        <div className="portal-overlay billing-case-action-overlay" onMouseDown={() => setCaseAction(null)}>
          <form
            className="portal-detail billing-case-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Nouvel avenant du dossier"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveAmendment}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCaseAction(null)}>×</button>
            <h2>Préparer un avenant</h2>
            <p className="section-intro">
              Cette étape photographie le contrat actuel. Aucun montant actif,
              devis ou facture externe n’est modifié avant votre décision.
            </p>
            <div className="form-grid">
              <label className="wide">Titre<input required value={amendmentDraft.title} onChange={(event) => setAmendmentDraft({ ...amendmentDraft, title: event.target.value })} placeholder="Ex. Extension du périmètre" /></label>
              <label>Montant actuel HT<input readOnly value={selected.contract_value_ex_vat == null ? "À compléter" : String(selected.contract_value_ex_vat)} /></label>
              <label>Nouveau total contractuel HT<input required type="number" min="0" step="0.01" value={amendmentDraft.revisedTotal} onChange={(event) => setAmendmentDraft({ ...amendmentDraft, revisedTotal: event.target.value })} /></label>
              <label className="wide">Devis accepté source, si disponible<select value={amendmentDraft.sourceQuoteId} onChange={(event) => setAmendmentDraft({ ...amendmentDraft, sourceQuoteId: event.target.value })}><option value="">Pièce externe ou justificatif à contrôler</option>{sourceQuoteCandidates.map((item) => <option value={item.id} key={item.id}>{item.label} · {item.detail}</option>)}</select></label>
              <p className="section-intro wide">Le devis choisi doit être accepté et correspondre soit à l’écart, soit au nouveau total. Sinon le serveur refuse l’avenant.</p>
              <label className="wide">Motif<textarea required value={amendmentDraft.reason} onChange={(event) => setAmendmentDraft({ ...amendmentDraft, reason: event.target.value })} /></label>
              {actionMessage && <p className="notice error wide">{actionMessage}</p>}
              <button disabled={busy}>{busy ? "Préparation…" : "Préparer sans appliquer"}</button>
            </div>
          </form>
        </div>
      )}

      {selected && selectedAmendment && caseAction === "amendmentDecision" && (
        <div className="portal-overlay billing-case-action-overlay" onMouseDown={() => setCaseAction(null)}>
          <section
            className="portal-detail billing-case-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Décision sur l’avenant"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCaseAction(null)}>×</button>
            <h2>Avenant n°{selectedAmendment.amendment_number}</h2>
            <p><strong>{selectedAmendment.title}</strong></p>
            <p>{selectedAmendment.reason}</p>
            <div className="stats billing-case-detail-stats">
              <article><span>Avant</span><strong>{money(selectedAmendment.previous_contract_value_ex_vat)}</strong></article>
              <article><span>Écart</span><strong>{selectedAmendment.change_amount_ex_vat >= 0 ? "+" : ""}{money(selectedAmendment.change_amount_ex_vat)}</strong></article>
              <article><span>Nouveau total</span><strong>{money(selectedAmendment.revised_contract_value_ex_vat)}</strong></article>
            </div>
            <label>Note de décision<textarea required value={amendmentDecisionNote} onChange={(event) => setAmendmentDecisionNote(event.target.value)} placeholder="Contrôles réalisés, pièce relue, motif de la décision…" /></label>
            {actionMessage && <p className="notice error">{actionMessage}</p>}
            <div className="row-actions">
              <button type="button" className="danger" disabled={busy} onClick={() => decideAmendment("reject")}>Refuser</button>
              <button type="button" disabled={busy} onClick={() => decideAmendment("approve")}>{busy ? "Décision…" : "Approuver et appliquer"}</button>
            </div>
          </section>
        </div>
      )}

      {createOpen && (
        <div className="portal-overlay" onMouseDown={() => setCreateOpen(false)}>
          <form
            className="portal-detail billing-case-create-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Nouveau dossier de facturation"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={createCase}
          >
            <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setCreateOpen(false)}>×</button>
            <h2>Nouveau dossier de facturation</h2>
            <p className="section-intro">Le mode simple reste léger ; le mode chantier active une lecture d’avancement détaillée.</p>
            <div className="form-grid">
              <label>Client<select required value={draft.clientId} onChange={(event) => setDraft({ ...draft, clientId: event.target.value, propertyId: "" })}><option value="">Choisir</option>{clients.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
              <label>Bien éventuel<select value={draft.propertyId} onChange={(event) => setDraft({ ...draft, propertyId: event.target.value })}><option value="">Aucun / client global</option>{availableProperties.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
              <label className="wide">Titre<input required value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder="Ex. Remise en état Villa Horizon" /></label>
              <label>Type<select value={draft.kind} onChange={(event) => setDraft({ ...draft, kind: event.target.value as "simple" | "project" })}><option value="simple">Dossier simple</option><option value="project">Projet / chantier</option></select></label>
              <label>Facturation<select value={draft.strategy} onChange={(event) => setDraft({ ...draft, strategy: event.target.value as BillingCaseSummary["billing_strategy"] })}><option value="grouped">Regroupée</option><option value="individual">Individuelle</option><option value="milestone">Par jalon</option><option value="progress">À l’avancement</option></select></label>
              <label>Montant contractuel HT<input type="number" min="0" step="0.01" value={draft.contractValue} onChange={(event) => setDraft({ ...draft, contractValue: event.target.value })} /></label>
              <label>Objectif de marge (%)<input type="number" min="0" max="99.99" step="0.01" value={draft.targetMargin} onChange={(event) => setDraft({ ...draft, targetMargin: event.target.value })} /></label>
              <label className="wide">Notes internes<textarea value={draft.notes} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} /></label>
              {message && <p className="notice error wide">{message}</p>}
              <button disabled={busy}>{busy ? "Création…" : "Créer le dossier"}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
